import React, { ChangeEvent, useState } from "react";
import cn from "classnames";

import Modal from "components/componentLibrary/Modals/SplitContentModal";
import Dropdown from "components/Dropdown";
import { titleize } from "util/strings";
import { analyticsEvents, track } from "util/analytics";
import { trackScheduleTourClick } from "util/ga4";
import SaveWrapper from "..";
import styles from "./styles.module.scss";

interface IProps {
  id: string;
  signUpType: string;
  source: string;
  type: string;
  marketOfInterest?: string | null;
  cityOfInterest?: string | null;
  areaExternalIds?: string[];
  btnClassName?: string;
  title?: string;
  "data-qa": string;
}

const TIMES = [
  "MONDAY___MORNING",
  "MONDAY___AFTERNOON",
  "TUESDAY___MORNING",
  "TUESDAY___AFTERNOON",
  "WEDNESDAY___MORNING",
  "WEDNESDAY___AFTERNOON",
  "THURSDAY___MORNING",
  "THURSDAY___AFTERNOON",
  "FRIDAY___MORNING",
  "FRIDAY___AFTERNOON",
];
const ANY_TIME = "ANY_TIME_AND_DAY_WORKS";

const formatTime = (time: string) =>
  time.split("___").map(titleize).join(" - ");

const getReadableTimes = (times: string[]) => {
  if (times[0] === ANY_TIME) return "Any day and time works";
  return times.map(formatTime).join(", ");
};

export const Tour = ({
  id,
  signUpType,
  source,
  type,
  btnClassName,
  title,
  marketOfInterest,
  cityOfInterest,
  areaExternalIds = [],
  ...props
}: IProps) => {
  const [showTourRequest, setShowTourRequest] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([ANY_TIME]);

  const onChangeTime = ({
    currentTarget: { value, checked },
  }: ChangeEvent<HTMLInputElement>) => {
    let times = [...selectedTimes];
    if (checked) {
      if (value === ANY_TIME) times = [ANY_TIME];
      else {
        times.push(value);
        times = times.filter((t) => t !== ANY_TIME);
      }
    }
    if (!checked) times = times.filter((t) => t !== value);
    setSelectedTimes(times);
  };

  return (
    <SaveWrapper
      inquiribleId={id}
      signUpType={signUpType}
      source={source}
      type={type}
      availableTimes={selectedTimes}
      marketOfInterest={marketOfInterest}
      cityOfInterest={cityOfInterest}
      areaExternalIds={areaExternalIds}
      onSuccess={() => {
        setShowTourRequest(false);
      }}
    >
      {(save, isSaved, loading) => (
        <>
          <button
            className={cn(btnClassName, "btn-outline")}
            type="button"
            data-qa={props["data-qa"]}
            disabled={isSaved}
            onClick={() => {
              track(analyticsEvents.clicked, {
                category: `${type} Page CTAs`,
                label: "Schedule a Tour",
              });
              trackScheduleTourClick({
                listingId: id,
                buildingName: title || "",
                city: cityOfInterest || "",
              });
              setShowTourRequest(true);
            }}
          >
            Schedule A Tour
          </button>
          {showTourRequest && (
            <Modal onClose={() => setShowTourRequest(false)}>
              <div className={styles.container}>
                <h1 className="title">Request a Tour</h1>
                <h2 className="subheader">{title}</h2>
                <form className={styles.form}>
                  <Dropdown
                    overlay={
                      <ul className={cn(styles.listOverflow, "checkboxes")}>
                        <li>
                          <label htmlFor="date-any">
                            <input
                              type="checkbox"
                              name="times"
                              value={ANY_TIME}
                              id="date-any"
                              checked={selectedTimes.indexOf(ANY_TIME) > -1}
                              onChange={onChangeTime}
                            />
                            Any time and day works
                          </label>
                        </li>
                        {TIMES.map((time) => (
                          <li key={time}>
                            <label htmlFor="date-any">
                              <input
                                type="checkbox"
                                name="times"
                                value={time}
                                id="date-any"
                                checked={selectedTimes.indexOf(time) > -1}
                                onChange={onChangeTime}
                              />
                              {formatTime(time)}
                            </label>
                          </li>
                        ))}
                      </ul>
                    }
                  >
                    <input
                      type="text"
                      readOnly
                      className={styles.input}
                      placeholder={
                        selectedTimes.length > 0
                          ? getReadableTimes(selectedTimes)
                          : "Choose date and time"
                      }
                    />
                  </Dropdown>
                  <button
                    type="button"
                    disabled={loading}
                    className="btn-primary"
                    onClick={save}
                  >
                    Request Tour
                  </button>
                </form>
                <p className="caption">
                  A SquareFoot broker will be in touch to confirm and schedule
                  your tour.
                </p>
              </div>
            </Modal>
          )}
        </>
      )}
    </SaveWrapper>
  );
};

export default Tour;
